/**
 * ViewDocument Modal component module.
 * @module shared/components/modals/content/ViewDocument
 *
 */

import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import BaseModal from '../types/Base';
import {_openModal, _removeModal} from '../../../store';
import PropTypes from 'prop-types';
import axiosInstance from '../../../utils/axios';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';
import {Button, LoadingSpinner, Row} from '../../../../shared';
import debug from 'debug';
import PDF from '../../PDF';
import history from '../../../../history';
import {setSectionURI, getShareText} from '../../../utils/utils';

const log = debug('circleblack:view-document-modal');

const _iframe = styled.iframe`
  width: 100%;
  height: 500px;
  background-color: white;
`;

/**
 * View Document modal component to view different types of documents.
 * @name ViewDocument
 */
const ViewDocument = props => {
  const {removeModal, content, openModal, session} = props;
  const [isMounted, setIsMounted] = useState(true);
  const [document, setDocument] = useState(content.document);
  const inputValue = {};
  const compliance = content.compliance;
  const [uri, setUri] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (document.cat === 'Insight') {
      axiosInstance.get(`insights/${document.catID}`, {params: {...inputValue}})
        .then(res => {
          res.data.content = res.data.content.substring(0, res.data.content.length - 4);
          res.data.content = res.data.content.replace(/<a/gi, '<a target="_blank" onclick="return cbOpenExternalLink()"');
          res.data.content = res.data.content.replace('<![CDATA[', '');
          res.data.content = res.data.content.replace(']]>', '');
          res.data.content = res.data.content.replace(/<\/p>/ig, '\n');
          res.data.content = res.data.content.replace(/<\/p/ig, '\n');
          res.data.content = res.data.content.replace(/<br\s*[\/]?>/gi, '\n');
          res.data.content = res.data.content.replace(/(?:<\/br>)+/ig, '\n');
          res.data.content = res.data.content.replace(/<p(?:.*?)><img/i, '<img');
          res.data.content = res.data.content.replace(/\/><p>/i, '/>');
          res.data.content = res.data.content.replace(/srcset="(?:.*?)"/g, '');
          document.content = res.data.content;
          document.HTML = sanitizeHtml(document.content);
          document.author = res.data.author;
          document.authorLink = res.data.authorLink;
          document.publishedDate = res.data.publishedDate;
          setLoading(false);
          setUri(res.data.uri);
        })
        .catch(function error(err) {
          log('error', err);
          const res = err.response;

          if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
            err.message = 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
          }

          openModal({
            type: 'Error',
            title: 'Failed to Retrieve Document',
            err: err,
          });
        });
    } else {
      axiosInstance.get(`documents/${document.id}/download?${compliance ? 'compliance=1' : ''}`, {params: {...inputValue}})
        .then(res => {
          setLoading(false);
          setUri(res.data.uri);
        })
        .catch(function error(err) {
          log('error', err);
          const res = err.response;

          if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
            err.message = 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
          }
          openModal({
            type: 'Error',
            title: 'Failed to Retrieve Document',
            err: err,
          });
        });
    }

    return () => {setIsMounted(false);};
  }, []);

  if (content.dataManager) {
    content.dataManager.updateActiveDocument = setDocument;
  }

  const {disableShareDetails, shareText} = getShareText(document, compliance, session.profile.id);

  return <BaseModal onClose={() => removeModal(content)} title={document.title} size={'lg'}>
    <Row margin='0 0 20px 0' justifySubRow='left'>
      {!content.hideDocumentDetails && <Button importance='secondary'
                                               size='md'
                                               label='Document Details'
                                               width='150px'
                                               action={() => {
                                                 openModal({
                                                   type: 'DocumentDetails',
                                                   document: document,
                                                   dataManager: content.dataManager,
                                                   compliance: content.compliance,
                                                   hideDocumentDetails: content.hideDocumentDetails,
                                                   isMounted: isMounted,
                                                 });
                                                 removeModal(content);
                                               }}
      />}
      <Button importance='secondary'
              size='md'
              label='Share Details'
              width='150px'
              action={() => {
                if (disableShareDetails) {
                  openModal({
                    type: 'MessageModal',
                    title: 'Share Details',
                    body: shareText,
                  });
                } else {
                  // TODO determine proper routing for investor here; can use content.hideDocumentDetails as investor condition
                  history.push(setSectionURI(`/${document.id}/sharing`));
                }
                removeModal(content);
              }}
              disabled={!!session.runAsInvestor}
      />
    </Row>
    {document.cat === 'Insight' && !loading
    && <div dangerouslySetInnerHTML={{__html: document.HTML}}/>}
    {(document.fileFormat === 'html' && uri) && !loading
    && <_iframe src={uri}
                type='text/html'
                loading={loading}
                frameBorder='0'>
    </_iframe>}
    {document.fileFormat === 'pdf' && uri && !loading
    && <PDF uri={uri}/>}
    {loading && <LoadingSpinner/>}
  </BaseModal>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  removeModal: modal => dispatch(_removeModal(modal)),
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewDocument);

ViewDocument.propTypes = {
  /**
     * Session
     */
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number.isRequired,
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  /**
   * content of the modal with modal component name
   */
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    /**
     * name of modal component
     */
    componentName: PropTypes.string.isRequired,
    /**
     * document object being displayed in the modal
     */
    document: PropTypes.object.isRequired,
    /**
     * dataManager for updating document
     */
    dataManager: PropTypes.object.isRequired,
    /**
     * If true passes compliance to the api indicating that it's on a compliance related page
     */
    compliance: PropTypes.bool,
    /**
     * If true, Document Details button is not displayed
     */
    hideDocumentDetails: PropTypes.bool,
  }),
  /**
   * The open modal function to dispatch _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  /**
   * The remove modal function to dispatches _removeModal from Redux
   */
  removeModal: PropTypes.func.isRequired,
};
